<script setup lang="ts">
const app = useNuxtApp()
const route = useRoute()
const kijidasConfig = useAppConfig().kijidas
const storeIndex = useStoreIndex()
const storeArticleList = useStoreArticleList()

definePageMeta({
  layout: 'layout-side-default',
  middleware: defineNuxtRouteMiddleware((to) => {
    const app = useNuxtApp()
    const storeIndex = useStoreIndex()
    storeIndex.init()
    const reqYear = app.$anyToInteger(to.params.year, null)
    if (reqYear && reqYear !== storeIndex.state.displayYear) {
      return navigateTo(`/schedule/${storeIndex.state.displayYear}/${to.params.slug}`, { redirectCode: 301 })
    }
  }),
  validate: async (route) => {
    const app = useNuxtApp()
    return app.$anyToInteger(route.params.year, null) ? true : false
  },
})

const pageData = {
  static: {
    title: '',
    html: '',
  },
  items: [] as any[],
  cityItems: {} as any,
}

const baseTitle = '展覧会スケジュール'
let headTitle = ''
let headDescription = ''

let year = 2025
let areaCategories = []
let areaCategory = {} as any
let cityCategories = []
const isHiddenPast = ref(true)
let isCurrentYear = false

const setHead = () => {
  headTitle = `${areaCategory.name}の${year}年の${baseTitle}`
  headDescription = `${headTitle}がひとめで分かります。主要な美術館・博物館・ミュージアムの年間スケジュールがずらり、旅行の予定にも！`
  // ぱんくず作成
  storeIndex.setBreadCrumbs([
    {
      text: baseTitle,
      disabled: false,
      to: { path: '/schedule' },
    },
    {
      text: areaCategory.name,
      disabled: true,
    },
  ])

  const headInput = app.$makeMeta({
    title: headTitle,
    description: headDescription,
  })
  useHead(headInput)
}

const getMuseumCountUnderCity = (citySlug) => {
  const item = pageData.cityItems[citySlug]
  if (item) {
    return item.count
  }
  return 0
}

const getMuseumItemsUnderCity = (citySlug) => {
  const item = pageData.cityItems[citySlug]
  if (item) {
    return item.museumItems
  }
  return []
}

const getMuseumItemsLengthUnderCity = (citySlug) => {
  const item = pageData.cityItems[citySlug]
  if (item) {
    return item.museumItems.length
  }
  return 0
}

const onClickToggleDisp = () => {
  isHiddenPast.value = !isHiddenPast.value
}

const scrollToTop = () => {
  window.scrollTo({ top: 0 })
}

const initPage = async () => {
  const areaCatSlug = route.params.slug
  year = app.$anyToInteger(route.params.year, null)
  const staticSlug = year + '_' + areaCatSlug

  // ============================================================
  // エリアカテゴリ / static の取得
  const [_resSite, _resMaster, resCategory, resStatic] = await Promise.all([
    storeIndex.fetchSite(),
    storeIndex.fetchMaster(),
    storeArticleList.fetchCategories('event'),
    storeArticleList.fetchList('scheduleStatic', { query: { article_slug: staticSlug } }),
  ])
  const eventCats = resCategory.data.Categories
  areaCategory = app.$getCategoryObjBySlug(eventCats, areaCatSlug)
  if (!areaCategory) {
    //  エリアカテゴリがない
    showError({ statusCode: 404 })
    return
  }
  //  全エリアカテゴリ取得
  areaCategories = app.$getChildCategoriesBySlug(eventCats, 'root_schedule_area')
  //  エリア配下cityカテゴリ取得
  cityCategories = app.$getChildCategoriesBySlug(eventCats, areaCatSlug)

  // staticがあれば設定
  if (resStatic.code === 200000 && resStatic.data?.articles?.length === 1) {
    const item = resStatic.data.articles[0] as Article
    const page = {}
    pageData.static.title = item.title
    pageData.static.html = app.$getBody(item.body_json)
  }

  // ============================================================
  // event fetch
  const areaCatId = areaCategory.id

  const dateAfter = useDayjs(year + '/01/01').startOf('year').format('YYYY/MM/DD 00:00:00')
  const dateBefore = useDayjs(year + '/01/01').endOf('year').format('YYYY/MM/DD 23:59:59')

  const options: KjdsFetchListOptions = {
    query: {
      category: areaCatId,
      dateAfter,
      dateBefore,
    },
  }

  const res = await storeArticleList.fetchList('scheduleEvent', options)
  if (res.code !== 200000) {
    showError({
      statusCode: 999,
      message: 'イベントデータ取得エラー',
    })
    return
  }

  if (!res.data?.articles?.length) {
    return
  }

  const items = res.data.articles as Article[]
  const museums = {}
  // {
  //   slug: {
  //     title
  //     url
  //     catCties
  //     count
  //     eventItems [{
  //       slug
  //       title
  //       term
  //       jyunkaiSlug
  //       reportSlug
  //       reportImSlug
  //        isPast
  //     }]
  //   }
  // }
  const cityList = {}
  // {
  //   slug: {
  //     title
  //     count
  //     museumItems [{
  //       slug
  //       title
  //       count
  //     }]
  //   }
  // }
  for (let i = 0; i < items.length; i++) {
    //  イベントをループして、museumの連想配列を作る
    const museumArticleSlug = items[i].custom.museum_article_slug
    if (!museums[museumArticleSlug]) {
      //  relationsからmuseumを探してrankを取得
      const relations = items[i].relations
      let rank
      relations.some((relItem) => {
        if (relItem.article.article_type_slug === 'museum' && relItem.article.article_slug === museumArticleSlug) {
          rank = relItem.article.rank
          if (!rank) {
            rank = 1
          }
          return true
        }
      })
      //  カテゴリーでareaCatSlugを親に持つcityカテゴリを取得
      const catCties = app.$getChildCategoriesById(items[i].categories, areaCatId)
      museums[museumArticleSlug] = {
        title: items[i].custom.museum_title,
        url: items[i].custom.custom_museum_url_jp,
        slug: items[i].custom.museum_article_slug,
        rank: rank,
        catCties: catCties,
        count: 0,
        eventItems: [],
      }
    }
    //  museum連想配列のevent配列に追加
    const term = app.$getPeriodLabel(items[i].term_from, items[i].term_to)
    const jyunkaiItem = app.$getArticleRelationsSingle(items[i], 'jyunkai', null)
    const jyunkaiSlug = jyunkaiItem ? jyunkaiItem.article_slug : null
    const reportItems = app.$getArticleRelationsMulti(items[i], 'report', null)
    let reportSlug = null
    let reportImSlug = null
    for (let i = 0; i < reportItems.length; i++) {
      const reportItem = reportItems[i]
      if (reportItem.custom.is_im_report === '1' || reportItem.custom.is_im_report === 1) {
        reportImSlug = reportItem.article_slug
      } else {
        reportSlug = reportItem.article_slug
      }
    }

    const diff = useDayjs().diff(items[i].term_to, 'day')
    let isPast = false
    if (diff > 60) {
      isPast = true
    } else {
      museums[museumArticleSlug].count++
    }
    const eventItem = {
      slug: items[i].article_slug,
      title: items[i].title,
      term: term,
      termFrom: items[i].term_from,
      termTo: items[i].term_to,
      isHideDayCount: items[i].day_count_display !== 1,
      jyunkaiSlug: jyunkaiSlug,
      reportSlug: reportSlug,
      reportImSlug: reportImSlug,
      isPast: isPast,
    }
    museums[museumArticleSlug].eventItems.push(eventItem)
  }
  // museumの連想配列から通常配列に変換
  let newItems = []
  for (const k in museums) {
    museums[k].slug = k
    newItems.push(museums[k])
  }
  //  rankでソート
  newItems = newItems.sort((a, b) => {
    return a.rank - b.rank
  })

  //  museum配列をループして city連想配列を作成
  for (let i = 0; i < newItems.length; i++) {
    const catCties = newItems[i].catCties
    for (let c = 0; c < catCties.length; c++) {
      const citySlug = catCties[c].category_slug
      if (!cityList[citySlug]) {
        cityList[citySlug] = {
          title: catCties[c].name,
          count: 0,
          museumItems: [],
        }
      }
      if (newItems[i].count > 0) {
        cityList[citySlug].count++
      }
      const museumItem = {
        slug: newItems[i].slug,
        title: newItems[i].title,
        count: newItems[i].count,
      }
      cityList[citySlug].museumItems.push(museumItem)
    }
  }

  pageData.items = newItems
  pageData.cityItems = cityList

  if (useDayjs().year() === year) {
    isCurrentYear = true
  }
  if (!isCurrentYear) {
    isHiddenPast.value = false
  }
}
await initPage()
setHead()
</script>

<template>
  <div
    class="p-schedule"
    v-bind:class="{ 'is-hiddenPast': isHiddenPast }"
  >
    <template
      v-if="pageData.static.title"
    >
      <h1 class="c-heading_page">
        {{ pageData.static.title }}
      </h1>
      <div
        class="p-schedule_static"
        v-html="pageData.static.html"
      />
    </template>
    <template
      v-else
    >
      <h1 class="c-heading_page">
        {{ areaCategory.name }}の{{ year }}年展覧会スケジュール
      </h1>
    </template>

    <div class="p-schedule_toc">
      <div
        v-if="isCurrentYear"
        class="p-schedule_toc_action"
      >
        <v-btn
          class="p-schedule_btnDisp"
          v-on:click="onClickToggleDisp"
        >
          <v-icon start>
            mdi-chevron-right
          </v-icon>
          <template
            v-if="isHiddenPast"
          >
            過去の展覧会も表示
          </template>
          <template
            v-else
          >
            最新の展覧会を表示
          </template>
        </v-btn>
      </div>

      <div class="p-schedule_toc_city">
        <template
          v-for="city in cityCategories"
        >
          <template
            v-if="getMuseumItemsLengthUnderCity(city.category_slug) > 0"
          >
            <dl
              v-bind:key="city.id"
              class="p-schedule_toc_city_item"
              v-bind:class="{ 'is-past': getMuseumCountUnderCity(city.category_slug) === 0 }"
            >
              <dt
                class="p-schedule_toc_city_item_name"
              >
                【{{ city.name }}】
              </dt>
              <dd class="p-schedule_toc_city_item_museum">
                <template
                  v-for="m in getMuseumItemsUnderCity(city.category_slug)"
                >
                  <span
                    v-if="!isHiddenPast || m.count !== 0"
                    v-bind:key="m.slug"
                    v-bind:class="{ 'is-past': m.count === 0 }"
                  >
                    <a
                      v-bind:href="'#' + m.slug"
                    >
                      {{ m.title }}
                    </a>
                  </span>
                </template>
              </dd>
            </dl>
          </template>
        </template>
      </div>
      <div class="p-schedule_toc_area">
        <nuxt-link
          v-for="area in areaCategories"
          v-bind:key="area.id"
          v-bind:to="{ name: 'schedule-year-slug', params: { year: year, slug: area.category_slug } }"
        >
          {{ area.name }}
        </nuxt-link>
      </div>
    </div>

    <div class="p-schedule_body">
      <div
        v-for="item in pageData.items"
        v-bind:key="item.slug"
        class="p-schedule_museum"
        v-bind:class="{ 'is-past': item.count === 0 }"
      >
        <h2 class="p-schedule_museum_head">
          <a
            v-bind:id="item.slug"
            class="p-schedule_museum_head_anchor"
          />
          <nuxt-link
            class="p-schedule_museum_head_name"
            v-bind:to="`/museum/${item.slug}`"
          >
            {{ item.title }}
          </nuxt-link>
          <a
            class="p-schedule_museum_head_site"
            v-bind:href="item.url"
            target="_blank"
          >
            {{ item.url }}
          </a>
          <a
            class="p-schedule_museum_head_top"
            href="javascript:void(0)"
            v-on:click="scrollToTop"
          >
            TOP
            <v-icon>mdi-menu-up</v-icon>
          </a>
        </h2>
        <div
          v-for="e in item.eventItems"
          v-bind:key="e.slug"
          class="p-schedule_museum_event"
          v-bind:class="{ 'is-past': e.isPast }"
        >
          <div class="p-schedule_museum_event_head">
            <nuxt-link
              class="p-schedule_museum_event_head_name"
              v-bind:to="`/event/${e.slug}`"
            >
              {{ e.title }}
            </nuxt-link>
            <nuxt-link
              v-if="e.jyunkaiSlug"
              class="p-schedule_museum_event_head_label is-jyunkai"
              v-bind:to="`/jyunkai/${e.jyunkaiSlug}`"
            >
              巡回展
            </nuxt-link>
            <nuxt-link
              v-if="e.reportSlug"
              class="p-schedule_museum_event_head_label is-report"
              v-bind:to="`/report/${e.reportSlug}`"
            >
              読者レポート
            </nuxt-link>
            <nuxt-link
              v-if="e.reportImSlug"
              class="p-schedule_museum_event_head_label is-reportIm"
              v-bind:to="`/report/${e.reportImSlug}`"
            >
              IMレポート
            </nuxt-link>
          </div>
          <div class="p-schedule_museum_event_term">
            {{ e.term }}
          </div>
          <PeriodRemain
            v-bind:term-from="e.termFrom"
            v-bind:term-to="e.termTo"
            v-bind:hide-day-count="e.isHideDayCount"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use "@/assets/css/foundation/mixin/responsive";

.p-schedule {
  position: relative;

  &_btnDisp {
    padding: 5px 20px;
    border: 2px solid #c5c6c7;
    border-radius: 8px;
    font-family: "Sawarabi Gothic", sans-serif;
    font-size: 14px;
    font-weight: 700;
    white-space: nowrap;
  }

  &_static {
    margin: 20px 0;
  }

  &_toc {
    margin-top: 20px;
    padding-top: 20px;
    border-top: 1px solid var(--g_color_border_gray);

    &_action {
      display: flex;
      justify-content: flex-end;
      margin-bottom: 10px;
    }

    &_city {
      &_item {
        margin-bottom: 30px;
        font-size: 14px;

        &_name {
          margin-bottom: 15px;
          font-weight: bold;
        }

        &_museum {
          display: flex;
          flex-wrap: wrap;

          span {
            line-height: 1.5;
          }

          span + span {
            &::before {
              content: "/";
              margin: 0 10px;
            }
          }
        }
      }
    }

    &_area {
      display: flex;
      flex-wrap: wrap;
      margin-top: 32px;

      a {
        display: inline-block;
        height: 48px;
        margin-right: 17px;
        margin-bottom: 20px;
        padding: 0 10px;
        transition: 0.3s;
        border: 1px solid var(--g_color_border_gray);
        color: var(--color_text_base);
        font-size: 16px;
        line-height: 46px;

        &:hover {
          transition: none;
          background-color: var(--color_hover_grey);
          text-decoration: none;
        }

        @include responsive.mq_down(sp) {
          height: 38px;
          margin-bottom: 10px;
          font-size: 14px;
          line-height: 36px;
        }
      }
    }
  }

  &_museum {
    &_head {
      display: flex;
      position: relative;
      flex-wrap: wrap;
      align-items: center;
      margin: 60px 0 0;
      padding: 16px 13px 16px 0;
      border-bottom: none;
      background-color: #f1f4f6;
      font-size: 16px;

      &_anchor {
        position: absolute;
        margin-top: -250px;
      }

      &_name {
        padding-left: 13px;
        border-left: 6px solid var(--color_primary);
        color: var(--color_text_base);
        font-size: 20px;
        font-weight: 400;

        @include responsive.mq_down(sp) {
          font-size: 18px;
        }
      }

      &_site {
        margin-left: 15px;
        font-size: 16px;

        @include responsive.mq_down(sp) {
          font-size: 14px;
        }
      }

      &_top {
        margin-left: auto;
        text-decoration: none;

        &:hover {
          opacity: 0.7;
          text-decoration: none;
        }
      }
    }

    &_event {
      padding: 22px 0 28px;
      border-bottom: 1px solid var(--g_color_border_gray);

      &_head {
        &_name {
          color: var(--color_text_base);
          font-size: 18px;
          font-weight: bold;

          @include responsive.mq_down(sp) {
            font-size: 16px;
          }
        }

        &_label {
          margin-left: 12px;
          padding: 2px 11px;
          color: #fff;
          font-size: 13px;
          font-weight: bold;
          line-height: 1.5;
          white-space: nowrap;

          &:hover {
            opacity: 0.7;
            text-decoration: none;
          }

          &.is-jyunkai {
            background-color: var(--color_jyunkai_author_bg);
          }

          &.is-report {
            background-color: var(--color_report_author_bg_user);
          }

          &.is-reportIm {
            background-color: var(--color_report_author_bg_im);
          }
        }
      }

      &_term {
        margin-top: 12px;
        margin-bottom: 8px;
        font-size: 14px;
      }
    }
  }

  &.is-hiddenPast {
    .p-schedule {
      &_toc_city_item {
        &.is-past {
          display: none;
        }

        &_museum {
          span {
            &.is-past {
              display: none;
            }
          }
        }
      }

      &_museum {
        &.is-past {
          display: none;
        }

        &_event {
          &.is-past {
            display: none;
          }
        }
      }
    }
  }
}
</style>
